import { useEffect, useState } from "react";
import { api, API, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, X } from "lucide-react";

export const DocLinesDialog = ({ docNo, report, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/reports/doc-lines", { params: { doc_no: docNo, report } })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [docNo, report]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/reports/doc-lines/export?doc_no=${encodeURIComponent(docNo)}&report=${report}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DeePiping_Doc_${docNo}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="doc-lines-backdrop">
      <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid="doc-lines-dialog"
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Document <span className="font-mono text-[#B01E24]">{docNo}</span> — line items</h3>
            {data && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                {data.count} line(s) · Net {formatINR(data.totals?.amount)} ·
                <span className={`ml-1.5 font-semibold ${data.source === "SAP" ? "text-emerald-700" : "text-amber-700"}`}>{data.source === "SAP" ? "SAP Live · Read-only" : "Mock demo"}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportExcel} disabled={exporting || !data} data-testid="doc-lines-export-button"
              className="flex items-center gap-1.5 text-xs border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md px-3 py-1.5 disabled:opacity-50">
              {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Excel
            </button>
            <button onClick={onClose} data-testid="doc-lines-close" className="text-slate-400 hover:text-slate-700 p-1"><X size={16} /></button>
          </div>
        </div>
        <div className="overflow-auto px-5 py-3">
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="doc-lines-error">{error}</div>}
          {!data && !error && <div className="text-slate-500 text-sm animate-pulse py-6">Loading document lines…</div>}
          {data?.note && <div className="text-[12px] text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 mb-2" data-testid="doc-lines-note">{data.note}</div>}
          {data && data.rows.length === 0 && <div className="text-sm text-slate-500 py-6" data-testid="doc-lines-empty">No journal lines found for this document.</div>}
          {data && data.rows.length > 0 && (
            <table className="w-full text-xs" data-testid="doc-lines-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-3">Line</th><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">GL Account</th>
                  <th className="py-2 pr-3">GL Name</th><th className="py-2 pr-3">Party</th><th className="py-2 pr-3">Material</th>
                  <th className="py-2 pr-3">Ref</th><th className="py-2 pr-3">D/C</th><th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-1.5 pr-3 font-mono">{r.item}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{r.date}</td>
                    <td className="py-1.5 pr-3 font-mono">{r.gl_account}</td>
                    <td className="py-1.5 pr-3">{r.gl_account_name}</td>
                    <td className="py-1.5 pr-3 font-mono">{r.party}</td>
                    <td className="py-1.5 pr-3 font-mono">{r.material}</td>
                    <td className="py-1.5 pr-3 font-mono">{r.ref_doc}</td>
                    <td className={`py-1.5 pr-3 ${r.dc === "Credit" ? "text-amber-700" : "text-emerald-700"}`}>{r.dc}</td>
                    <td className="py-1.5 text-right font-mono">{formatINR(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocLinesDialog;
